<template>
  <base-slide-over ref="slideOver" title="Edit Activity Type" @hiding="reset()" size="xl">
    <base-form ref="form" v-model="isFormValid" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6">
      <base-text-field label="Label" type="text" placeholder="Enter a label for the activity type" v-model="todoItemType.label" @keyup.enter="save" required />
      <base-text-field
        label="Group name"
        type="text"
        placeholder="Enter a group name for the activity type"
        v-model="todoItemType.typeGroup"
        @keyup.enter="save"
        class="tw-pb-5"
      />
      <base-textarea label="Description" placeholder="Enter a description" v-model="todoItemType.description" @keyup.enter="save" required />
      <div class="tw-mt-3">
        <base-advanced-select
          label="Medications"
          placeholder="Select one or more Medications"
          :options="groupedAndSortedMedications"
          :value="selectedMedications"
          @input="onMedicationsChange"
          label-key="label"
          value-key="value"
          multiple
          class="tw-flex-grow"
        />
      </div>
      <div class="tw-mt-4">
        <div v-if="isLoadingRxItems">
          <span>Loading Rx Items...</span>
        </div>
        <base-advanced-select
          v-else
          label="Default Rx Items"
          placeholder="Select one or more Rx Items"
          :options="groupedAndSortedRxItems"
          :value="selectedRxItems"
          @input="onRxItemsChange"
          label-key="label"
          value-key="value"
          multiple
          class="tw-flex-grow"
        />
      </div>
      <div class="tw-my-3 tw-flex tw-justify-end">
        <base-button @click="toggleCondition" type="flat">
          {{ showCondition ? "Cancel Condition" : "Add Condition" }}
        </base-button>
      </div>
      <div v-if="showCondition" class="tw-mb-6">
        <div v-for="(condition, index) in conditionalItems" :key="index" class="tw-flex tw-flex-col tw-gap-4 tw-mb-3 tw-p-3 tw-border tw-border-gray-300 tw-rounded">
          <base-text-field label="SIG Code Text" type="text" placeholder="Enter a text used to match SIG code" v-model="condition.sigCode" class="tw-flex-1" />
          <base-advanced-select
            v-model="condition.selectedRxItems"
            label="Rx Items"
            placeholder="Select one or more Rx Items"
            :options="groupedAndSortedRxItems"
            label-key="label"
            value-key="value"
            multiple
            class="tw-flex-1"
          />
        </div>
      </div>

      <div class="tw-mt-3">
        <base-checkbox label="Allow assign" :true-value="true" :false-value="false" v-model="todoItemType.isAssignable" />
      </div>
      <inline-error class="tw-mt-3" id="edit-activity-type-modal-error" :title="errorTitle" :message="errorMessage" :error="error" />
    </base-form>
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
        <base-button @click="hide" type="flat" class="tw-w-auto tw-mr-3">
          Cancel
        </base-button>
        <base-button @click="save" :loading="isLoading" class="tw-w-auto" :disabled="!isFormValid">
          Save
        </base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import InlineError from "@/components/InlineError";
import orderBy from "lodash/orderBy";

export default {
  name: "EditTodoItemType",
  components: { InlineError },
  props: {
    businessId: String,
    allRxItems: {
      type: Array,
      default: () => [],
    },
    allMedications: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isLoading: false,
      isFormValid: true,
      isLoadingRxItems: false,
      todoItemType: {
        todoItemTypeId: null,
        label: null,
        typeGroup: null,
        description: null,
        target: "member",
        isAssignable: false,
        businessItemCategoryIds: null,
        medicationIds: null,
      },
      selectedRxItems: [],
      selectedMedications: [],
      errorTitle: "",
      errorMessage: "",
      error: null,
      showCondition: false,
      conditionalItems: [],
    };
  },
  computed: {
    groupedAndSortedRxItems() {
      if (!Array.isArray(this.allRxItems) || this.allRxItems.length === 0) {
        return [];
      }
      const suppliesItems = this.allRxItems.filter((item) => item.productGroup === "Supplies");
      const mappedItems = suppliesItems.map((item) => ({
        ...item,
        label: typeof item.name === "string" && item.name.trim() !== "" ? item.name : item.label && item.label.trim() !== "" ? item.label : "Unnamed Rx Item",
        value: String(item.businessItemCategoryId),
        group: item.productGroup || null,
      }));

      const options = [];
      const groups = [...new Set(mappedItems.map((item) => item.group))].filter(Boolean);

      groups.forEach((group) => {
        options.push({ label: group, value: null, isHeader: true });
        const itemsInGroup = mappedItems.filter((i) => i.group === group).map((i) => ({ label: i.label, value: i.value }));
        options.push(...orderBy(itemsInGroup, ["label"]));
      });

      const noGroupItems = mappedItems.filter((item) => !item.group);
      if (noGroupItems.length > 0) {
        options.push({ label: "Other Rx Items", value: null, isHeader: true });
        const mappedNoGroup = noGroupItems.map((i) => ({
          label: i.label,
          value: i.value,
        }));
        options.push(...orderBy(mappedNoGroup, ["label"]));
      }
      return options;
    },
    groupedAndSortedMedications() {
      if (!Array.isArray(this.allMedications) || this.allMedications.length === 0) {
        return [];
      }
      const mappedItems = this.allMedications.map((item) => ({
        ...item,
        label: typeof item.name === "string" && item.name.trim() !== "" ? item.name : item.label && item.label.trim() !== "" ? item.label : "Unnamed Medication",
        value: String(item.businessItemCategoryId),
        group: item.productGroup || null,
      }));

      const options = [];
      const groups = [...new Set(mappedItems.map((item) => item.group))].filter(Boolean);

      const sortedGroups = orderBy(groups, [(group) => group.toLowerCase()], ["asc"]);

      sortedGroups.forEach((group) => {
        options.push({ label: group, value: null, isHeader: true });
        const itemsInGroup = mappedItems.filter((i) => i.group === group).map((i) => ({ label: i.label, value: i.value }));
        options.push(...orderBy(itemsInGroup, [(item) => (isNaN(item.label) ? item.label.toLowerCase() : parseFloat(item.label))], ["asc"]));
      });

      const noGroupItems = mappedItems.filter((item) => !item.group);
      if (noGroupItems.length > 0) {
        options.push({ label: "Other Medications", value: null, isHeader: true });
        const mappedNoGroup = noGroupItems.map((i) => ({
          label: i.label,
          value: i.value,
        }));
        options.push(...orderBy(mappedNoGroup, [(item) => (isNaN(item.label) ? item.label.toLowerCase() : parseFloat(item.label))], ["asc"]));
      }

      return options;
    },
  },
  methods: {
    reset() {
      this.todoItemType = {
        todoItemTypeId: null,
        label: null,
        typeGroup: null,
        description: null,
        target: "member",
        isAssignable: false,
        businessItemCategoryIds: null,
        medicationIds: null,
      };
      this.selectedRxItems = [];
      this.selectedMedications = [];
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;
      this.showCondition = false;
      this.conditionalItems = [];
      if (this.$refs.form && typeof this.$refs.form.reset === "function") this.$refs.form.reset();
    },
    show(todoItemType) {
      this.todoItemType = { ...todoItemType };

      // Parse and normalize Rx selected IDs as strings
      let selectedRxIds = [];
      if (this.todoItemType.businessItemCategoryIds && typeof this.todoItemType.businessItemCategoryIds === "string") {
        try {
          selectedRxIds = JSON.parse(this.todoItemType.businessItemCategoryIds);
        } catch (e) {
          selectedRxIds = [];
        }
      } else if (Array.isArray(this.todoItemType.businessItemCategoryIds)) {
        selectedRxIds = [...this.todoItemType.businessItemCategoryIds];
      }
      selectedRxIds = selectedRxIds.map(String);
      this.selectedRxItems = selectedRxIds
        .map((id) => {
          const match = this.allRxItems.find((item) => String(item.businessItemCategoryId) === id);
          if (match) {
            return {
              label: typeof match.name === "string" && match.name.trim() !== "" ? match.name : match.label || "Unnamed Rx Item",
              value: String(match.businessItemCategoryId),
            };
          }
          return { label: "Unnamed Rx Item", value: id };
        })
        .filter(Boolean);
      let medicationIds = [];
      if (this.todoItemType.medicationIds) {
        if (Array.isArray(this.todoItemType.medicationIds)) {
          medicationIds = this.todoItemType.medicationIds;
        } else if (typeof this.todoItemType.medicationIds === "string") {
          try {
            medicationIds = JSON.parse(this.todoItemType.medicationIds);
          } catch (e) {
            console.error("Failed to parse medicationIds JSON:", e.message);
            medicationIds = [];
          }
        }
      }
      this.selectedMedications = medicationIds
        .map((id) => {
          const match = this.allMedications.find((med) => String(med.businessItemCategoryId) === String(id));
          if (match) {
            return {
              label: match.name || match.label || "Unnamed Medication",
              value: String(match.businessItemCategoryId),
            };
          }
          return null;
        })
        .filter(Boolean);

      // Reset conditional items
      this.conditionalItems = [];
      this.showCondition = false;

      // Parse conditionals as before
      let conditionalItemsParsed = [];

      if (todoItemType.conditionalItems) {
        if (typeof todoItemType.conditionalItems === "string") {
          try {
            conditionalItemsParsed = JSON.parse(todoItemType.conditionalItems);
          } catch (e) {
            conditionalItemsParsed = [];
            console.error("Failed to parse conditionalItems JSON:", e.message);
          }
        } else if (Array.isArray(todoItemType.conditionalItems)) {
          conditionalItemsParsed = todoItemType.conditionalItems;
        }
      }

      // Always ensure 2 pairs
      for (let i = 0; i < 2; i++) {
        if (conditionalItemsParsed[i]) {
          const cond = conditionalItemsParsed[i];
          this.conditionalItems.push({
            sigCode: cond.SigCode || "",
            selectedRxItems: (cond.RxItemIds || [])
              .map((id) => {
                const match = this.allRxItems.find((item) => String(item.businessItemCategoryId) === String(id));
                if (match) {
                  return {
                    label: typeof match.name === "string" && match.name.trim() !== "" ? match.name : match.label || "Unnamed Rx Item",
                    value: String(match.businessItemCategoryId),
                  };
                }
                return null;
              })
              .filter(Boolean),
          });
        } else {
          // Add empty pair if missing
          this.conditionalItems.push({
            sigCode: "",
            selectedRxItems: [],
          });
        }
      }

      if (conditionalItemsParsed !== null && conditionalItemsParsed.length > 0) {
        this.showCondition = true;
      }

      this.$refs.slideOver.show();
    },
    hide() {
      this.$refs.slideOver.hide();
      this.reset();
    },
    save() {
      if (this.error) {
        this.error = null;
      }

      const invalid = this.conditionalItems.some((cond) => {
        // If either sigCode has value OR selectedRxItems has values, then both must be non-empty
        const sigCodeFilled = cond.sigCode && cond.sigCode.trim() !== "";
        const rxItemsFilled = Array.isArray(cond.selectedRxItems) && cond.selectedRxItems.length > 0;
        return (sigCodeFilled && !rxItemsFilled) || (!sigCodeFilled && rxItemsFilled);
      });

      if (invalid) {
        this.errorTitle = "Validation error";
        this.errorMessage = "Please ensure all conditions have both SIG Code Text and Rx Items filled out, or leave both empty.";
        this.error = new Error("Validation Failed: Incomplete condition pairs.");
        return;
      }

      if (!this.$refs.form.validate()) return;
      this.isLoading = true;

      const businessItemCategoryIds = this.selectedRxItems
        .map((item) => (typeof item === "string" || typeof item === "number" ? String(item) : item && item.value ? String(item.value) : null))
        .filter((id) => id !== null);

      const medicationIds = this.selectedMedications
        .map((item) => (typeof item === "string" || typeof item === "number" ? String(item) : item && item.value ? String(item.value) : null))
        .filter((id) => id !== null);

      const conditionalItemObjects = this.conditionalItems
        .map((cond) => ({
          sigCode: cond.sigCode,
          rxItemIds: (cond.selectedRxItems || [])
            .map((item) => (typeof item === "string" || typeof item === "number" ? String(item) : item && item.value ? String(item.value) : null))
            .filter((id) => id !== null),
        }))
        .filter((cond) => cond.sigCode && cond.rxItemIds.length > 0);

      this.$axios
        .put(`/businesses/${this.businessId}/todo/types/${this.todoItemType.todoItemTypeId}`, {
          label: this.todoItemType.label,
          typeGroup: this.todoItemType.typeGroup,
          description: this.todoItemType.description,
          target: this.todoItemType.target,
          isAssignable: this.todoItemType.isAssignable,
          businessItemCategoryIds,
          medicationIds,
          conditionalItems: conditionalItemObjects,
        })
        .then(() => {
          this.$emit("typesUpdated");
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            title: "Activity Type Modified",
            message: "Your activity type was saved.",
          });
          this.hide();
        })
        .catch((err) => {
          this.errorTitle = "Error editing activity type";
          this.errorMessage = "Sorry, we had trouble updating this activity type. Please try again.";
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onRxItemsChange(selected) {
      this.selectedRxItems = Array.isArray(selected) ? selected : [];
    },
    onMedicationsChange(selected) {
      this.selectedMedications = Array.isArray(selected) ? selected : [];
    },
    toggleCondition() {
      if (this.showCondition) {
        // Cancel/reset
        this.resetConditions();
      } else {
        // Always show exactly 2 pairs when enabled
        this.showCondition = true;
        this.conditionalItems = [{ sigCode: "", selectedRxItems: [] }, { sigCode: "", selectedRxItems: [] }];
      }
    },
    resetConditions() {
      this.showCondition = false;
      this.conditionalItems = [];
    },
  },
};
</script>
